// ChatsPage.tsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Avatar,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Divider,
  CircularProgress,
  Badge,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/system";
import AttachmentIcon from "@mui/icons-material/Attachment";
import SendIcon from "@mui/icons-material/Send";
import PreviewImageModal from "../../components/PreviewImage/PreviewImageModal";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom"; // <-- Changed here
import { getChats, getChatDetails, sendMessage } from "./actions";
import {
  ChatDetailsViewModel,
  ChatMessageType,
  SendChatMessageCommand,
} from "../../configuration/api/apiSchema";
import { HandymanAppState } from "../../configuration/redux/reducers";
import { AppDispatch } from "../../App";
import ChatMessageEvent from "../../components/ChatMessageEvent/ChatMessageEvent";
import ChatIcon from "@mui/icons-material/Chat";
import CancelIcon from "@mui/icons-material/Cancel";

// Styled component for message bubbles
const MessageBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isOwn",
})<{ isOwn: boolean }>(({ theme, isOwn }) => ({
  padding: theme.spacing(1.5, 2),
  maxWidth: "100%",
  alignSelf: isOwn ? "flex-end" : "flex-start",
  backgroundColor: isOwn ? theme.palette.primary.main : "#ffffff",
  color: isOwn ? "#ffffff" : theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
  borderRadius: isOwn ? theme.spacing(2, 2, 0, 2) : theme.spacing(2, 2, 2, 0),
  boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
  position: "relative",
  border: isOwn ? "none" : "1px solid rgba(0,0,0,0.1)",
}));

const ChatsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>(); // typed dispatch
  const navigate = useNavigate();
  const { chatId } = useParams();              // <-- Get `chatId` from the URL
  const currentUser = useSelector(
    (state: HandymanAppState) => state.user.currentUserDetails
  );
  const { chats, chatDetails, loading } = useSelector(
    (state: HandymanAppState) => state.chat
  );
  const userRole = useSelector(
    (state: HandymanAppState) => state.user.currentUserDetails?.roleId
  );

  // Local state for search input (by username)
  const [searchTerm, setSearchTerm] = useState("");

  // Filter chats by searchTerm (username)
  const filteredChats = useMemo(() => {
    let list = chats;
    if (searchTerm.trim() !== "") {
      list = list.filter((chat: any) =>
        chat.participants?.some((p: any) =>
          p.userDetails?.fullName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      );
    }
    return list;
  }, [chats, searchTerm]);

  // Local states for message input & attachments
  const [newMessageText, setNewMessageText] = useState("");
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Fetch all chats on mount (if user is logged in)
  useEffect(() => {
    if (currentUser) {
      dispatch(getChats());
    }
  }, [dispatch, currentUser]);

  // Fetch chat details when `chatId` changes
  useEffect(() => {
    if (chatId) {
      dispatch(getChatDetails(chatId));
    }
  }, [dispatch, chatId]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleAttachmentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachmentFile(file);
  };

  const handleSendMessage = async () => {
    if (!newMessageText.trim() && !attachmentFile) return;
    if (!chatId) return; // Only send if we have a valid chatId

    let attachmentBase64 = "";
    if (attachmentFile) {
      attachmentBase64 = await fileToBase64(attachmentFile);
    }

    const messageCommand: SendChatMessageCommand = {
      chatMessageType: ChatMessageType.User,
      content: newMessageText,
      attachmentBase64,
    };

    dispatch(sendMessage(chatId, messageCommand));
    setNewMessageText("");
    setAttachmentFile(null);
  };

  // Render a single message bubble
  const renderMessage = (item: any) => {
    if (item.chatMessageType === ChatMessageType.Event && item.eventDetails) {
      return (
        <ChatMessageEvent 
          key={item.id} 
          event={item.eventDetails}
          roleId={userRole}
          orderStatus={item.eventDetails.orderStatus}
        />
      );
    }

    const isOwn = item.senderId === currentUser?.id;
    const sender = item.sender || {};
    const hasAttachment = item.attachmentBase64 && item.attachmentBase64.length > 0;
    
    return (
      <Box
        key={item.id}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: isOwn ? "flex-end" : "flex-start",
          mb: 2,
          maxWidth: "85%",
          alignSelf: isOwn ? "flex-end" : "flex-start",
        }}
      >
        {/* Sender info */}
        <Box 
          sx={{
            display: "flex", 
            alignItems: "center", 
            mb: 0.5,
            gap: 1,
            order: 1,
            alignSelf: isOwn ? "flex-end" : "flex-start",
          }}
        >
          {!isOwn && (
            <Avatar
              src={sender.avatarUrl || undefined}
              alt={`${sender.firstName} ${sender.lastName}` || "Sender"}
              sx={{ 
                width: 24, 
                height: 24,
                border: "1px solid",
                borderColor: "primary.main",
              }}
            >
              {!sender.avatarUrl && (sender.firstName?.charAt(0) || "?")}
            </Avatar>
          )}
          <Typography 
            variant="caption" 
            sx={{ 
              color: "text.secondary",
              fontSize: "0.75rem",
            }}
          >
            {sender.firstName} {sender.lastName} • {new Date(item.timestamp).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Typography>
        </Box>

        {/* Message content */}
        <MessageBubble isOwn={isOwn} elevation={0}>
          {/* Text content */}
          {item.content && (
            <Typography 
              variant="body1" 
              sx={{ 
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "0.9375rem",
                lineHeight: 1.4,
                mb: hasAttachment ? 1 : 0
              }}
            >
              {item.content}
            </Typography>
          )}
          
          {/* Image attachment */}
          {hasAttachment && (
            <Box 
              sx={{
                position: "relative",
                mt: item.content ? 1 : 0,
                "& img": {
                  maxWidth: "100%",
                  maxHeight: 300,
                  borderRadius: 1,
                  display: "block",
                }
              }}
            >
              <img
                src={item.attachmentBase64}
                alt="attachment"
                style={{ 
                  cursor: "pointer",
                  border: isOwn ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.1)",
                }}
                onClick={() => {
                  setSelectedImage(item.attachmentBase64);
                  setIsImageModalOpen(true);
                }}
              />
            </Box>
          )}

          {/* Message status */}
          {item.status && (
            <Typography 
              variant="caption" 
              sx={{ 
                display: "block",
                textAlign: "right",
                mt: 0.5,
                opacity: 0.7,
                fontSize: "0.675rem",
                color: isOwn ? "rgba(255,255,255,0.8)" : "text.secondary",
              }}
            >
              {item.status.toUpperCase()}
            </Typography>
          )}
        </MessageBubble>
      </Box>
    );
  };

  function getChatTitle(chat: ChatDetailsViewModel, currentUserId?: string) {
    // 1) If we have a non-empty chat.title, use that.
    if (chat.title && chat.title.trim() !== "") {
      return chat.title;
    }
  
    // 2) Otherwise, attempt to find the *other* user's name:
    if (chat.participants && chat.participants.length > 0 && currentUserId) {
      // If only 2 participants, find the "other" user:
      if (chat.participants.length === 2) {
        const other = chat.participants.find(
          (p: any) => p.userDetails?.id !== currentUserId
        );
        if (other && other.userDetails?.firstName && other.userDetails?.lastName) {
          return other.userDetails?.firstName + ' ' + other.userDetails?.lastName;
        }
      }
      // If more than 2 participants, show them all, or fallback
      const names = chat.participants
        .map((p: any) => p.userDetails?.fullName)
        .filter(Boolean)
        .join(", ");
      return names || "Group Chat";
    }
  
    return "No Title";
  }

  // Skeleton loader for chat list items
  const ChatListSkeleton = () => (
    <>
      {[1, 2, 3].map((i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid #eee",
          }}
        >
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Box flexGrow={1}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={20} />
          </Box>
          <Skeleton variant="text" width={40} />
        </Box>
      ))}
    </>
  );

  // Skeleton loader for chat messages
  const ChatMessagesSkeleton = () => (
    <>
      {[1, 2, 3].map((i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: i % 2 === 0 ? "flex-end" : "flex-start",
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
              flexDirection: i % 2 === 0 ? "row-reverse" : "row",
            }}
          >
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton
              variant="text"
              width={100}
              sx={{ mx: 1 }}
            />
          </Box>
          <Skeleton
            variant="rounded"
            width="60%"
            height={60}
            sx={{
              borderRadius: 2,
              ml: i % 2 === 0 ? "auto" : 0,
            }}
          />
        </Box>
      ))}
    </>
  );

  return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #f5f5f5, #ffffff)",
        minHeight: "100vh",
        py: 10,
      }}
    >
      <PreviewImageModal
        base64SelectedImage={selectedImage}
        isImageModalOpen={isImageModalOpen}
        handleCloseImageModal={() => {
          setIsImageModalOpen(false);
          setSelectedImage(null);
        }}
      />
      <Box
        width={{ xs: "90%", sm: "80%", md: "75%" }}
        mx="auto"
        mt={4}
        component={Paper}
        elevation={4}
        sx={{ 
          borderRadius: 4,
          display: 'flex',
          height: '80vh',
          overflow: 'hidden'
        }}
      >
        {/* Left Sidebar: Chat List */}
        <Box
          sx={{
            width: { xs: "100%", sm: 300 },
            borderRight: "1px solid #ddd",
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box p={2} borderBottom="1px solid #ddd">
            <Typography variant="h6" gutterBottom>
              Chats
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search chats..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mt: 1 }}
            />
          </Box>
          <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
            {loading ? (
              <ChatListSkeleton />
            ) : (
              filteredChats.map((chat) => (
                <Box
                  key={chat.id}
                  onClick={() => navigate(`/chats/${chat.id}`)}
                  sx={{
                    p: 2,
                    borderBottom: "1px solid #eee",
                    backgroundColor: chatId === chat.id ? "rgba(0,0,0,0.05)" : "transparent",
                    cursor: "pointer",
                    transition: 'background-color 0.2s',
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.1)" },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                      sx={{ width: 48, height: 48 }}
                      src={chat.participants?.find(
                        (p: any) => p.userDetails?.id !== currentUser?.id
                      )?.userDetails?.avatarUrl || undefined}
                    >
                      {getChatTitle(chat, currentUser?.id).charAt(0)}
                    </Avatar>
                    <Box flexGrow={1} overflow="hidden">
                      <Typography variant="subtitle1" noWrap>
                        {getChatTitle(chat, currentUser?.id)}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {chat.lastMessage || 'No messages'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {chat.updatedAt && new Date(chat.updatedAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>

        {/* Right Side: Chat Messages */}
        <Box sx={{ 
          flexGrow: 1, 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f8f9fa'
        }}>
          {chatId ? (
            <>
              {/* Chat Header */}
              <Box 
                sx={{ 
                  p: 2, 
                  borderBottom: '1px solid #ddd',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Avatar
                  sx={{ width: 40, height: 40 }}
                  src={chatDetails?.participants?.find(
                    (p: any) => p.userDetails?.id !== currentUser?.id
                  )?.userDetails?.avatarUrl || undefined}
                >
                  {chatDetails && getChatTitle(chatDetails, currentUser?.id).charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {chatDetails && getChatTitle(chatDetails, currentUser?.id)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {chatDetails?.participants?.find(
                      (p: any) => p.userDetails?.id !== currentUser?.id
                    )?.userDetails?.firstName ? 'Online' : 'Offline'}
                  </Typography>
                </Box>
              </Box>

              {/* Messages Container */}
              <Box
                sx={{
                  flexGrow: 1,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  p: 3,
                  gap: 2
                }}
              >
                {loading ? (
                  <ChatMessagesSkeleton />
                ) : (
                  chatDetails?.messages?.map((msg) => renderMessage(msg))
                )}
              </Box>

              {/* Message Input */}
              <Box
                sx={{
                  p: 2,
                  borderTop: "1px solid #ddd",
                  backgroundColor: 'white',
                }}
              >
                {attachmentFile && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'rgba(0,0,0,0.04)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <img
                      src={URL.createObjectURL(attachmentFile)}
                      alt="attachment preview"
                      style={{
                        width: 40,
                        height: 40,
                        objectFit: 'cover',
                        borderRadius: 4
                      }}
                    />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {attachmentFile.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setAttachmentFile(null)}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center"
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    variant="outlined"
                    size="small"
                    placeholder="Type your message..."
                    value={newMessageText}
                    onChange={(e) => setNewMessageText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!newMessageText.trim() && !attachmentFile}
                  >
                    <SendIcon />
                  </IconButton>
                  <IconButton color="primary" component="label">
                    <AttachmentIcon />
                    <input
                      type="file"
                      hidden
                      onChange={handleAttachmentChange}
                      accept="image/*"
                    />
                  </IconButton>
                </Box>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2
              }}
            >
              <ChatIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
              <Typography variant="h6" color="text.secondary">
                Select a chat to start messaging
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatsPage;
